import { cloneDeep } from 'lodash-es';
import { AuditItem } from '@/models/audit';
import { createAuditPageHeaders } from '@/pages/system/audit/AuditPageHeaders';
import { auditService } from '@/services/auditService';
import { isRequestCanceled } from '@/services/baseService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IPageOptions } from '@/types';
import { IAuditFilters } from '@/types/audit';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createAuditStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<AuditItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IAuditFilters>({
        auditTableId: {
            value: null,
            title: 'audit.table',
            allowSaveState: true,
        },
        auditSourceId: {
            value: null,
            title: 'audit.source',
            allowSaveState: true,
        },
        auditActionId: {
            value: null,
            title: 'audit.action',
            allowSaveState: true,
        },
        auditUserId: {
            value: null,
            title: 'audit.user',
            allowSaveState: true,
        },
        auditName: {
            value: null,
            title: 'audit.name',
            allowSaveState: true,
        },
        auditId: {
            value: null,
            title: 'audit.id',
            allowSaveState: true,
        },
        auditObjectId: {
            value: null,
            title: 'audit.objectId',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        filters.auditTableId.value = null;
        filters.auditSourceId.value = null;
        filters.auditActionId.value = null;
        filters.auditUserId.value = null;
        filters.auditName.value = null;
        filters.auditId.value = null;
        filters.auditObjectId.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createAuditPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'audit',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;

        try {
            const response = await auditService.list({
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AuditTable'],
            });

            items.value = (response.data.rows || []).map((row) => new AuditItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
    };
}

export function useAuditStore(storeId: string) {
    const useStore = defineStore(storeId, () => createAuditStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'audit', defaultSortBy),
    });
    return useStore();
}

export type AuditStoreSetup = ReturnType<typeof useAuditStore>;
